// M5-R10 (docs/build-briefs/M5-R10-price-change-history-screen.md; DEC-355):
// the price history bottom sheet. Built at runtime and appended to the page,
// not written into index.html, because check:chrome-ratchet (ADR-004
// Decisions 2 and 5) refuses a new scrim-shaped overlay there. Read only: it
// draws what the server returned and offers Close and Done. The caller loads
// the history (so the floor and the closeout share one sheet) and passes the
// result in; sign-out closes it through closePriceHistorySheet().

import { NOT_AVAILABLE, type HistoryCardView, type PriceHistoryView } from "./priceHistoryModel.ts";

export type PriceHistoryLoad =
  { ok: true; view: PriceHistoryView } | { ok: false; message: string };

export interface PriceHistorySheetOptions {
  /** The drink's name, drawn after "Price history". */
  title: string;
  /** The ticket's name, with the date on the closeout. */
  subtitle: string;
  load: () => Promise<PriceHistoryLoad>;
  /** Called after the sheet closes, to give focus back to the opener. */
  onClosed?: () => void;
}

const SCRIM_ID = "price-history-scrim";

let scrim: HTMLElement | undefined;
let generation = 0;
let onClosed: (() => void) | undefined;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  if (testId !== undefined) {
    node.dataset["testid"] = testId;
  }
  return node;
}

function field(label: string, value: string): HTMLElement {
  const wrap = el("div");
  const dd = el("dd", value === NOT_AVAILABLE ? "ph-na" : "", value, "price-history-field-value");
  wrap.append(el("dt", undefined, label), dd);
  return wrap;
}

function tagNode(kind: string, text: string): HTMLElement {
  return el("span", `ph-tag ph-tag-${kind}`, text, `price-history-tag-${kind}`);
}

function cardNode(card: HistoryCardView): HTMLElement {
  const item = el("li", "ph-ev", undefined, "price-history-card");
  item.dataset["number"] = String(card.number);
  if (card.isPutBack) {
    item.classList.add("ph-putback");
  }
  if (card.isNow) {
    item.classList.add("ph-now");
  }
  item.append(el("span", "ph-dot", String(card.number)));
  const body = el("div", "ph-card");

  const when = el("div", "ph-when");
  when.append(el("span", "ph-time", card.time));
  if (card.isPutBack) {
    when.append(tagNode("put", "Put back"));
  }
  if (card.isNow) {
    when.append(tagNode("now", "Now"));
  }
  const money = el("div", "ph-money");
  money.append(
    el("span", "ph-from", card.fromText, "price-history-from"),
    el("span", "ph-to", card.toText, "price-history-to"),
    el("span", "ph-diff", card.diffText, "price-history-diff"),
  );
  const top = el("div", "ph-top");
  top.append(when, money);
  body.append(top);

  const grid = el("dl", "ph-grid");
  grid.append(
    field(card.actorLabel, card.actor),
    field("Approved by", card.approver),
    field("Reason", card.reason),
  );
  body.append(grid);
  if (card.tags.length > 0) {
    const flags = el("div", "ph-flags");
    for (const tag of card.tags) {
      flags.append(tagNode(tag.kind, tag.text));
    }
    body.append(flags);
  }
  item.append(body);
  return item;
}

function summaryNode(view: PriceHistoryView): HTMLElement | undefined {
  const s = view.summary;
  if (s === null) {
    return undefined;
  }
  const box = el("div", "ph-sum", undefined, "price-history-summary");
  const cell = (label: string, value: string, small: string, testId: string): HTMLElement => {
    const wrap = el("div");
    const v = el("div", "ph-sum-v", value, testId);
    if (small !== "") {
      v.append(" ", el("small", undefined, small));
    }
    wrap.append(el("div", "ph-sum-k", label), v);
    return wrap;
  };
  box.append(
    cell("Menu price", s.menuText, "", "price-history-menu"),
    cell("Now", s.nowText, s.nowDiffText, "price-history-now"),
    cell("Changes", s.changesText, s.putBackText, "price-history-changes"),
  );
  return box;
}

function renderBody(body: HTMLElement, load: PriceHistoryLoad, retry: () => void): void {
  body.replaceChildren();
  if (!load.ok) {
    const failed = el("div", "ph-failed", undefined, "price-history-error");
    failed.append(el("p", undefined, `Could not load the price history. ${load.message}`));
    const button = el("button", "btn-secondary", "Try again", "price-history-retry");
    button.type = "button";
    button.addEventListener("click", retry);
    failed.append(button);
    body.append(failed);
    return;
  }
  if (load.view.empty) {
    body.append(el("div", "ph-empty", "No price changes recorded yet.", "price-history-empty"));
    return;
  }
  const summary = summaryNode(load.view);
  if (summary !== undefined) {
    body.append(summary);
  }
  const rail = el("ol", "ph-tl", undefined, "price-history-rail");
  for (const card of load.view.cards) {
    rail.append(cardNode(card));
  }
  body.append(rail);
}

function build(title: string, subtitle: string): HTMLElement {
  const root = el("div", "scrim", undefined, SCRIM_ID);
  root.id = SCRIM_ID;
  root.hidden = true;
  const sheet = el("div", "sheet price-history-sheet", undefined, "price-history-sheet");
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-modal", "true");
  sheet.setAttribute("aria-labelledby", "price-history-title");

  const header = el("div", "sheet-header");
  const row = el("div", "sheet-title-row");
  const titleEl = el("span", "sheet-title", `Price history \u00b7 ${title}`, "price-history-title");
  titleEl.id = "price-history-title";
  row.append(titleEl);
  const x = el("button", "btn-icon", "✕", "price-history-close");
  x.type = "button";
  x.setAttribute("aria-label", "Close");
  x.addEventListener("click", () => closePriceHistorySheet());
  row.append(x);
  header.append(row, el("div", "sheet-subtitle", subtitle, "price-history-subtitle"));

  const body = el("div", "ph-body", undefined, "price-history-body");
  const foot = el("div", "sheet-footer");
  const done = el("button", "btn-primary btn-full", "Done", "price-history-done");
  done.type = "button";
  done.addEventListener("click", () => closePriceHistorySheet());
  foot.append(done);

  sheet.append(header, body, foot);
  root.append(sheet);
  root.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closePriceHistorySheet();
    }
  });
  return root;
}

export function isPriceHistorySheetOpen(): boolean {
  return scrim !== undefined && !scrim.hidden;
}

/** Opens the sheet and loads the history. A load that finishes after the sheet
 * was closed or reopened for another drink is dropped. */
export function openPriceHistorySheet(options: PriceHistorySheetOptions): void {
  scrim?.remove();
  scrim = build(options.title, options.subtitle);
  onClosed = options.onClosed;
  document.body.append(scrim);
  generation += 1;
  const mine = generation;
  const body = scrim.querySelector<HTMLElement>(".ph-body") as HTMLElement;
  const run = (): void => {
    body.replaceChildren(el("div", "ph-loading", "Loading", "price-history-loading"));
    void options.load().then((result) => {
      if (generation !== mine || scrim === undefined || scrim.hidden) {
        return;
      }
      renderBody(body, result, run);
    });
  };
  scrim.hidden = false;
  scrim.querySelector<HTMLElement>("[data-testid='price-history-done']")?.focus();
  run();
}

/** Closes and removes the sheet. Safe when it was never opened. */
export function closePriceHistorySheet(): void {
  if (scrim === undefined) {
    return;
  }
  generation += 1;
  scrim.remove();
  scrim = undefined;
  const after = onClosed;
  onClosed = undefined;
  after?.();
}
